import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  TextInput,
  PasswordInput,
  Button,
  Checkbox,
  Select,
  LoadingOverlay,
  Group,
  Text,
  Title,
} from "@mantine/core";
import { IconEye, IconEyeOff, IconInfoCircle } from "@tabler/icons-react";
import ApiSession from "../../api";
import Tooltip from "../../utils/helpers/Tooltip";
import { useDualTranslation } from "../../utils/i18nHelpers";
import { validatePasswordAgainstNames } from "../../utils/helpers";

const apiSession = new ApiSession();
const phoneNumberRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/;
const hostUrl = `${window.location.protocol}//${window.location.host}`;
const privacyPolicy =
  "https://www.se.com/ww/en/about-us/legal/data-privacy.jsp";

const normalizeSpaces = (val = "") => val.replace(/ +(?= )/g, "");

function UserRegistration({ location }) {
  const { t } = useDualTranslation();
  const [onLoadingStatus, setOnLoadingStatus] = useState(false);
  const [tokenExpireErrorMessage, setTokenExpireErrorMessage] = useState(false);
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [employer, setEmployer] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [locationVal, setLocationVal] = useState("");
  const [password, setPassword] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [registrationError, setRegistrationError] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [userAlreadyRegistered, setUserAlreadyRegistered] = useState(false);
  const [agreeStatus, setAgreeStatus] = useState(false);
  const [passwordCheck, setPasswordCheck] = useState([]);
  const passwordValid = passwordCheck.length === 0 && password !== "";
  const [displayPassword, setDisplayPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });
  const RawBusinessTypeList = {
    businessTypeList: [
      "End User",
      "Contractor",
      "Original Equipment Manufacturers",
      "Panel Builder",
      "Consumer Retailer",
      "Electrician",
      "System Integrator",
      "Specifier",
      "IT Reseller",
      "B2B Distribution",
      "Digital & Service Partner",
      "Internal Schneider Entity",
    ],
  };
  useEffect(() => {
    callSnackbar("error", t("userManagement.registration.fillMandatory"));

    setOnLoadingStatus(true);
    let querySearch = location?.search || window.location.search;
    let query = querySearch.split("?userId=");
    let token = query[1];
    setUserId(token);
    getTokenDetails(token);
  }, []);

  const getTokenDetails = (token) => {
    apiSession
      .getRegistrationDetails(token)
      .then((response) => {
        setEmail(response);
        setOnLoadingStatus(false);
      })
      .catch(() => {
        setTokenExpireErrorMessage(true);
        setEmail("");
        setOnLoadingStatus(false);
      });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value.replace(/\s/g, "");
    setPassword(value);
    const { reasons } = validatePasswordAgainstNames({ password: value, firstName, lastName });
    setPasswordCheck(reasons);
  };

  const handleChangeConfirmPassword = (e) => {
    setConfirmPassword(e.target.value.replace(/ +(?= )/g, ""));
  };

  // Validation derived states
  // Show mismatch when a password has been entered and confirmPassword is missing or different
  const passwordsMismatch = password !== "" && password !== confirmPassword;
  // Form validity (required fields + password rules + agreement)
  const formValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    locationVal.trim() !== "" &&
    passwordValid &&
    businessType !== "" &&
    employer.trim() !== "" &&
    agreeStatus === true &&
    password === confirmPassword;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      if ((value.length >= 2 && phoneNumberRegex.test(value)) || value.length < 2) {
        setPhoneNumber(value);
      }
      return;
    }
    if (name === "businessType") {
      setBusinessType(value);
      return;
    }
    if (name === "firstName" || name === "lastName") {
      if (/^[a-zA-Z0-9_&\s-]*$/.test(value)) {
        const normalized = normalizeSpaces(value);
        name === "firstName" ? setFirstName(normalized) : setLastName(normalized);
      }
      return;
    }
    if (name === "location") {
      setLocationVal(normalizeSpaces(value));
      return;
    }
    if (name === "employer") {
      setEmployer(normalizeSpaces(value));
    }
  };

  const reset = useCallback(() => {
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setEmployer("");
    setBusinessType("");
    setLocationVal("");
    setPassword("");
    setConfirmPassword("");
    setPasswordCheck([]);
  }, []);

  const clearFormAndLoading = useCallback(() => {
    setEmail("");
    reset();
    setOnLoadingStatus(false);
  }, [reset]);

  const handleCheckBoxChange = (e) => {
    setAgreeStatus(e.target.checked);
  };
  const callSnackbar = (errorType, Body) => {
    setNotification({ show: true, type: errorType, message: Body });
    if (document && document.getElementById("snackbar-register")) {
      document.getElementById("snackbar-register").open = true;
    }
  };

  const saveRegistration = () => {
    if (
      firstName !== "" &&
      passwordValid &&
      businessType !== "" &&
      employer !== "" &&
      agreeStatus !== false &&
      password === confirmPassword
    ) {
      setOnLoadingStatus(true);
      let data = {
        firstName,
        lastName,
        phoneNumber,
        employer,
        businessType,
        location: locationVal,
        password,
      };
      apiSession
        .saveRegistration(data, userId)
        .then(() => {
          setRegistrationSuccess(true);
          setRegistrationError(false);
          setUserAlreadyRegistered(false);
          clearFormAndLoading();
        })
        .catch((error) => {
          if (error.response?.status === 302) {
            setUserAlreadyRegistered(true);
          } else {
            setRegistrationError(true);
          }
          clearFormAndLoading();
        });
    } else {
      callSnackbar("error", t("userManagement.registration.fillMandatory"));
    }
  };

  const showPassword = () => {
    setDisplayPassword((prev) => !prev);
  };

  const disableEvent = (e) => {
    e.preventDefault();
  };

  return (
    <>
      <se-loading loading={onLoadingStatus} />

      <Container
        style={{
          width: "100%",
          maxWidth: 650,
          minWidth: 360,
          margin: "0 auto",
          padding: 24,
          boxSizing: "border-box",
        }}
      >
        {/* <LoadingOverlay visible={onLoadingStatus} overlayBlur={2} /> */}

        {tokenExpireErrorMessage && email === "" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "red",
              marginBottom: 12,
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <span>
              <se-icon size="medium">notification_critical_stroke</se-icon>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: "1.6em",
                  position: "relative",
                  top: "-2px",
                }}
              >
                {t("userManagement.registration.linkExpired")}
                <br />
                <a
                  href={hostUrl}
                  title={t("userManagement.registration.clickHereToLogin")}
                  style={{ fontSize: "1.3rem" }}
                >
                  {t("userManagement.registration.clickHereToLogin")}
                </a>
              </span>
            </span>
          </div>
        )}
        {registrationError && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "red",
              marginBottom: 12,
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <span>
              <se-icon size="medium">notification_critical_stroke</se-icon>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: "1.6em",
                  position: "relative",
                  top: "-2px",
                }}
              >
                {t("userManagement.registration.error")}
                <br />
                <a
                  href={hostUrl}
                  title={t("userManagement.registration.clickHereToLogin")}
                  style={{ fontSize: "1.3rem" }}
                >
                  {t("userManagement.registration.clickHereToLogin")}
                </a>
              </span>
            </span>
          </div>
        )}
        {userAlreadyRegistered && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#3dcd58",
              marginBottom: 12,
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <span>
              <se-icon size="medium">notification_critical_stroke</se-icon>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: "1.6em",
                  position: "relative",
                  top: "-2px",
                }}
              >
                {t("userManagement.registration.alreadyRegistered")}
                <br />
                <a href={hostUrl} title={t("userManagement.registration.clickHereToLogin")}>
                  {t("userManagement.registration.clickHereToLogin")}
                </a>
              </span>
            </span>
          </div>
        )}
        {registrationSuccess && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#3dcd58",
              marginBottom: 12,
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <span>
              <se-icon size="medium">notification_critical_stroke</se-icon>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: "1.6em",
                  position: "relative",
                  top: "-2px",
                }}
              >
                {t("userManagement.registration.success")}
                <br />
                <a href={hostUrl} title={t("userManagement.registration.clickHereToLogin")}>
                  {t("userManagement.registration.clickHereToLogin")}
                </a>
              </span>
            </span>
          </div>
        )}
        {tokenExpireErrorMessage !== true && email !== "" && (
          <>
            <Title
              order={2}
              align="center"
              mb="xs"
              style={{ color: "#3dcd58" }}
            >
              {t("userManagement.registration.title")}
            </Title>
            <Title
              order={4}
              align="center"
              mb="xs"
              style={{ color: "#3dcd58" }}
            >
              {t("userManagement.registration.subtitle")}
            </Title>
            <Text align="center" mb="md">
              {t("userManagement.registration.description")}
            </Text>
            <form autoComplete="off">
              <div style={{ display: "flex", alignItems: "center" }}>
                <TextInput
                  label={t("userManagement.registration.email")}
                  value={email}
                  disabled
                  leftSection={<IconInfoCircle size={16} />}
                  mb="sm"
                  style={{ flex: 1 }}
                />
                <span
                  style={{
                    marginLeft: 8,
                    cursor: "pointer",
                    verticalAlign: "middle",
                  }}
                >
                  <Tooltip
                    position="top"
                    iconColor="secondary"
                    information={t("userManagement.registration.emailTooltip")}
                  />
                </span>
              </div>
              <TextInput
                label={t("userManagement.registration.firstName")}
                name="firstName"
                value={firstName}
                maxLength={50}
                placeholder={t("userManagement.registration.firstName")}
                required
                onChange={handleChange}
                mb="sm"
                error={firstName.trim() === "" ? true : false}
              />
              <TextInput
                label={t("userManagement.registration.lastName")}
                name="lastName"
                value={lastName}
                maxLength={50}
                placeholder={t("userManagement.registration.lastName")}
                error={lastName.trim() === "" ? true : false}
                required
                onChange={handleChange}
                mb="sm"
              />
              <PasswordInput
                label={t("userManagement.registration.password")}
                name="password"
                value={password}
                maxLength={50}
                placeholder={t("userManagement.registration.password")}
                required
                onChange={handlePasswordChange}
                leftSection={
                  displayPassword ? (
                    <IconEyeOff size={16} />
                  ) : (
                    <IconEye size={16} />
                  )
                }
                visible={displayPassword}
                onVisibilityChange={showPassword}
                onPaste={disableEvent}
                onCopy={disableEvent}
                onCut={disableEvent}
                error={
                  password !== "" && passwordCheck.length > 0
                    ? passwordCheck.join(" ")
                    : password === ""
                    ? true
                    : false
                }
                mb="sm"
                autoComplete="off"
              />
              <PasswordInput
                label={t("userManagement.registration.confirmPassword")}
                name="confirmPassword"
                value={confirmPassword}
                maxLength={50}
                placeholder={t("userManagement.registration.confirmPassword")}
                required
                onChange={handleChangeConfirmPassword}
                onPaste={disableEvent}
                onCopy={disableEvent}
                onCut={disableEvent}
                error={passwordsMismatch ? t("userManagement.registration.passwordsDoNotMatch") : false}
                aria-invalid={passwordsMismatch}
                aria-describedby={passwordsMismatch ? 'password-match-error' : undefined}
                mb="sm"
                autoComplete="off"
              />
              <TextInput
                label={t("userManagement.registration.location")}
                name="location"
                value={locationVal}
                maxLength={50}
                placeholder={t("userManagement.registration.location")}
                required
                onChange={handleChange}
                error={locationVal.trim() === "" ? true : false}
                mb="sm"
              />
              <TextInput
                label={t("userManagement.registration.phoneNumber")}
                name="phoneNumber"
                value={phoneNumber}
                maxLength={14}
                placeholder={t("userManagement.registration.phoneNumber")}
                required
                error={phoneNumber.trim() === "" ? true : false}
                onChange={handleChange}
                mb="sm"
              />
              <TextInput
                label={t("userManagement.registration.companyName")}
                name="employer"
                value={employer}
                maxLength={50}
                placeholder={t("userManagement.registration.companyName")}
                required
                onChange={handleChange}
                error={employer.trim() === "" ? true : false}
                mb="sm"
              />
              <Select
                label={t("userManagement.registration.businessType")}
                name="businessType"
                value={businessType}
                placeholder={t("userManagement.registration.businessTypePlaceholder")}
                required
                onChange={(event) => setBusinessType(event)}
                data={RawBusinessTypeList.businessTypeList.map(
                  (businessData) => ({
                    value: businessData,
                    label: businessData,
                  })
                )}
                error={businessType === "" ? true : false}
                mb="sm"
                withinPortal
                searchable
              />
              <Group mt="md" mb="xs">
                <Checkbox
                  label={
                    <span>
                      {t("userManagement.registration.checkboxTextPrefix")} {" "}
                      <a
                        href={privacyPolicy}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("privacy.policyTitle")}
                      </a>
                    </span>
                  }
                  checked={agreeStatus}
                  onChange={handleCheckBoxChange}
                  required
                  color="green"
                />
              </Group>
              <Group justify="center" mt="md">
                <div style={{ width: '100%', maxWidth: 650 }}>
                  <div
                    id="password-match-error"
                    role="alert"
                    aria-live="assertive"
                    style={{ color: !formValid ? 'red' : 'transparent', minHeight: 20, textAlign: 'center', marginBottom: 8 }}
                  >
                    {!formValid ? t("userManagement.registration.fillMandatory") : ''}
                  </div>
                <Button
                  color="seGreen.0"
                  style={{ minWidth: 400, display: "block", margin: "0 auto" }}
                  disabled={!formValid}
                  onClick={saveRegistration}
                >
                  {t("userManagement.registration.register")}
                </Button>
                </div>
              </Group>
              <Group justify="center" mt="xs">
                <Button
                  variant="subtle"
                  color="#007bff"
                  component="a"
                  href={hostUrl}
                  style={{ minWidth: 200, display: "block", margin: "0 auto" }}
                >
                  {t("userManagement.registration.returnToLogin")}
                </Button>
              </Group>
            </form>
          </>
        )}
        {notification.show && (
          <se-snackbar
            id="snackbar-register"
            type={notification.type === "error" ? "error" : "success"}
            icon="information_stroke"
            message={notification.message}
            duration="3000"
          ></se-snackbar>
        )}
      </Container>
    </>
  );
}

export default UserRegistration;
